import { useState, useContext, useRef, useEffect } from "react";

import * as yup from "yup";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useHistory, useParams } from "react-router-dom";

import sotApi from 'api/sotApi';
import PromptAlert from "components/PromptAlert";
import LoadingPanel from "components/common/TabPanel/LoadingPanel";
import RuleContainer from "../../RuleContainer/RuleContainer";
import useTextRuleJson from "components/sourcedocs/Rules/useTextRuleJson";
import BaseAOOHHeadlinesForm from "./BaseAOOHHeadlinesForm";
import { parseText } from "components/sourcedocs/Rules/utils";
import { SnackbarContext } from "context/SnackbarContext";
import { LP } from "components/sourcedocs/Rules/RuleConstants";
import { useSelectionRule } from "../../MarketingBullets/hooks/useSelectionRule";
import { RulesContextProvider } from "../../Rules/context/RulesContext";
import { useSubheads } from "../hooks/useSubheads";
import { getSubheadQueryResults } from "../hooks/subheadUtils";

const schema = yup.object().shape({
    refid: yup.string().required("The Ref. id field is required.").max(7, "Ref ID cannot be more than 7 characters"),
    template_text: yup.string(),
    disclaimer_text: yup.string(),
});

const parseSelectionRule = (data, baseSelectionRuleJSON, selectionRuleJSON, setters) => {
    const {
        setBaseSelectionRuleJSON,
        setSelectionRuleJSON,
    } = setters;
    try {
        if (typeof data.selectionrule_json === 'string') {
            data.selectionrule_json = JSON.parse(data.selectionrule_json);
        }
        if (!data.selectionrule_json) {
            return;
        }
        if (data.selectionrule_json.base_operator) {
            const tempBaseSelectionRuleJSON = { ...baseSelectionRuleJSON };
            for (const field in baseSelectionRuleJSON) {
                tempBaseSelectionRuleJSON[field] = data.selectionrule_json[field];
            }
            setBaseSelectionRuleJSON(tempBaseSelectionRuleJSON);
            const exprsIndex = data.selectionrule_json.exprs?.findIndex((exprs) => !exprs.baseRules);
            if (exprsIndex >= 0) {
                setSelectionRuleJSON({ ...data.selectionrule_json.exprs[exprsIndex] });
            }
        } else if (data.selectionrule_json.type === "group") {
            setSelectionRuleJSON({ ...selectionRuleJSON, ...data.selectionrule_json });
        } else if (data.selectionrule_json.exprs) {
            setSelectionRuleJSON({ ...selectionRuleJSON, ...data.selectionrule_json.exprs });
        }
    } catch (error) {
        // ignore malformed rule json
    }
};

const AOOHHeadlinesForm = () => {
    const { id, action } = useParams();
    const templateId = useRef(null);
    const isCloneFlow = action === 'clone';
    const originalRefid = useRef(null);

    const {
        selectionRuleJSON,
        setSelectionRuleJSON,
        baseSelectionRuleJSON,
        setBaseSelectionRuleJSON,
        getSelectionRuleToSend
    } = useSelectionRule();

    const {
        subheads,
        loadSubheads,
        resetSubheadsForClone,
        addSubhead,
        removeSubhead,
        updateSubheadText,
        syncSubheads,
    } = useSubheads();

    const [isLoading, setLoading] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);
    const [headlineObj, setHeadlineObj] = useState(null);
    const { textRulesJSON, setTextRulesJSON, handleVariableChange } = useTextRuleJson();

    const { handleSubmit, control, watch, setValue, setError, formState } = useForm({
        mode: 'all',
        resolver: yupResolver(schema),
        defaultValues: {
            refid: '',
            template_text: '',
            disclaimer_text: '',
        },
    });

    const history = useHistory();
    const watchRefId = watch('refid');
    const { setSnackBarConfig } = useContext(SnackbarContext);

    const handleSelectionRuleJSONChange = (obj) => {
        setSelectionRuleJSON(obj);
        setIsBlocking(true);
    };

    const handleBaseSelectionRuleJSONChange = (obj) => {
        setBaseSelectionRuleJSON(obj);
        setIsBlocking(true);
    };

    const handleTextRulesJSONChange = (obj, blocking = true) => {
        setTextRulesJSON(obj);
        setIsBlocking(blocking);
    };

    useEffect(() => {
        const load = async () => {
            if (!id) {
                return;
            }
            setLoading(true);
            try {
                const res = await sotApi.aoohHeadlines.get(id);
                const data = res.data;

                if (isCloneFlow) {
                    setHeadlineObj(null);
                    templateId.current = null;
                    originalRefid.current = null;
                    setValue('refid', '');
                } else {
                    setHeadlineObj(data);
                    templateId.current = data.aooh_headline_template_id;
                    originalRefid.current = data.refid;
                    setValue('refid', data.refid);
                }

                setValue('template_text', data.template_text || '');
                setValue('disclaimer_text', data.disclaimer_text || '');

                parseSelectionRule(data, baseSelectionRuleJSON, selectionRuleJSON, {
                    setBaseSelectionRuleJSON,
                    setSelectionRuleJSON,
                });

                let textRule = data.text_rule_json;
                if (typeof textRule === 'string') {
                    textRule = JSON.parse(textRule);
                }
                setTextRulesJSON(textRule || { vars: [], blocks: [] });

                if (isCloneFlow) {
                    const subheadRes = await sotApi.aoohSubheads.getByHeadlineRefid(data.refid);
                    resetSubheadsForClone(getSubheadQueryResults(subheadRes));
                } else {
                    await loadSubheads(data.refid);
                }

                setIsBlocking(false);
            } catch (error) {
                setSnackBarConfig({
                    open: true,
                    message: 'Failed to load AOOH headline.',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        const subscription = watch(() => {
            setIsBlocking(true);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const submitHeadline = async (payload) => {
        try {
            setLoading(true);
            if (templateId.current) {
                await sotApi.aoohHeadlines.update(templateId.current, payload);
            } else {
                const res = await sotApi.aoohHeadlines.add(payload);
                templateId.current = res.data.aooh_headline_template_id;
            }
            await syncSubheads(payload.refid, originalRefid.current);
            setSnackBarConfig({ open: true, message: 'Saved successfully.', severity: 'success' });
            setIsBlocking(false);
            history.push({
                pathname: "/sourcedocs/aoohHeadlines",
                state: { importSuccess: true },
            });
        } catch (error) {
            if (error.response?.status === 400 && error.response.data?.refid) {
                setError('refid', {
                    type: "custom",
                    message: error.response.data.refid[0],
                });
            } else {
                setSnackBarConfig({
                    open: true,
                    message: 'Something went wrong while saving.',
                    severity: 'error',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = (data) => {
        const selectionRuleToSend = getSelectionRuleToSend();
        const payload = {
            refid: data.refid?.trim()?.slice(0, 7),
            template_text: data.template_text,
            disclaimer_text: data.disclaimer_text ?? '',
            selectionrule_json: JSON.stringify(selectionRuleToSend),
            text_rule_json: JSON.stringify(textRulesJSON),
        };
        submitHeadline(payload);
    };

    const handleTextBlur = (e) => {
        handleVariableChange(parseText(e.target.value, textRulesJSON.vars));
    };

    return (
        <RulesContextProvider>
            {isLoading && <LoadingPanel />}
            <form onSubmit={handleSubmit(onSubmit)} data-testid="aooh-headlines-form">
                <Stack direction="row" justifyContent="space-between">
                    <div>
                        <h4 style={{ color: '#8F8A92', fontSize: '14px', margin: 0 }}>
                            AOOH HEADLINES
                        </h4>
                        <h3 style={{ color: '#050307', fontSize: '29px', margin: 0, textTransform: "capitalize" }}>
                            {action}
                        </h3>
                    </div>
                </Stack>
                <div className="new-wrapper">
                    <div style={{ padding: "30px 30px 0" }}>
                        <Button variant="contained" type="submit">
                            Save Changes
                        </Button>
                    </div>
                    <div style={{ padding: 30 }}>
                        <BaseAOOHHeadlinesForm
                            dataObj={headlineObj}
                            control={control}
                            formState={formState}
                            onTextBlur={handleTextBlur}
                            subheads={subheads}
                            onAddSubhead={addSubhead}
                            onRemoveSubhead={removeSubhead}
                            onSubheadTextChange={updateSubheadText}
                        />
                    </div>
                </div>
                <div className="new-wrapper">
                    <div style={{ marginTop: 30, padding: "10px" }}>
                        <RuleContainer
                            refText={watchRefId || "Reference ID"}
                            textRulesJSON={textRulesJSON}
                            setTextRulesJSON={handleTextRulesJSONChange}
                            selectionRuleJSON={selectionRuleJSON}
                            setSelectionRuleJSON={handleSelectionRuleJSONChange}
                            baseSelectionRuleJSON={baseSelectionRuleJSON}
                            setBaseSelectionRuleJSON={handleBaseSelectionRuleJSONChange}
                            hideBaseSelectionFieldset
                            fixedPlan={LP}
                        />
                    </div>
                </div>
                <PromptAlert isBlocking={isBlocking} />
            </form>
        </RulesContextProvider>
    );
};

export default AOOHHeadlinesForm;
